import React from "react";
import styled, { css } from "styled-components";

import { breakpoints } from "../index";
import { colors } from "../colors";

import CheckMarkApprove from "../../icons/CheckMarkApprove";

// input
const inputStylesBySize = {
    small: css`
        font-size: 0.75rem;
        line-height: 0.75rem;
        padding: 0.3125rem 0.4375rem;

        &:hover {
            padding: calc(0.3125rem + 1px) calc(0.4375rem + 1px);
        }

        &:hover {
            &:focus,
            &:read-only {
                padding: 0.3125rem 0.4375rem;
            }
        }

        &:disabled {
            padding: calc(0.3125rem + 2px) calc(0.4375rem + 2px);
        }
    `,
    medium: css`
        font-size: 0.875rem;
        line-height: 0.875rem;
        padding: 0.75rem;

        &:hover {
            padding: calc(0.75rem + 1px);
        }

        &:hover {
            &:focus,
            &:read-only {
                padding: 0.75rem;
            }
        }

        &:disabled {
            padding: calc(0.75rem + 2px);
        }
    `,
    large: css`
        font-size: 1.125rem;
        line-height: 1.125rem;
        padding: 0.9375rem;

        &:hover {
            padding: calc(0.9375rem + 1px);
        }

        &:hover {
            &:focus,
            &:read-only {
                padding: 0.9375rem;
            }
        }

        &:disabled {
            padding: calc(0.9375rem + 2px);
        }
    `
};

export const Input = styled.input`
    ${({ inverse, size }) => css`
        ${inputStylesBySize.medium}
        color: ${inverse ? colors.whiteDuplicate : colors.black};
        border: 3px solid ${inverse ? "#3B3B46" : colors.veryLightPink};
        font-family: inherit;
        width: 100%;
        background-color: ${inverse ? "transparent" : colors.whiteDuplicate};

        @media (min-width: ${breakpoints.sm}rem) {
            ${size && inputStylesBySize[size.sm]}
        }

        @media (min-width: ${breakpoints.md}rem) {
            ${size && inputStylesBySize[size.md]}
        }

        @media (min-width: ${breakpoints.lg}rem) {
            ${size && inputStylesBySize[size.lg]}
        }

        &::placeholder {
            color: ${inverse ? colors.brownGrey : colors.brownishGreyTwo};
        }

        &:hover {
            border: 2px solid
                ${inverse ? colors.brownGrey : colors.brownishGreyTwo};
        }

        &:focus {
            border: 3px solid ${
                inverse ? "transparent" : colors.whiteDuplicate
            };
            outline: 3px auto rgba(40, 143, 240, 1);
        }

        &:disabled {
            color: ${inverse ? colors.brownGrey : colors.brownishGreyTwo};
            background-color: ${
                inverse ? colors.charcoalGreyTwo : colors.veryLightPink
            };
            border: 1px solid
                ${
                    inverse
                        ? colors.charcoalGrey
                        : colors.veryLightPinkTwoDuplicate
                };
        }

        &:read-only {
            border: 3px solid ${
                inverse ? "transparent" : colors.whiteDuplicate
            };
            color: ${inverse ? colors.whiteDuplicate : colors.black};
        }
    `}
`;

export const TextArea = styled.textarea`
    ${({ inverse, size }) => css`
        ${inputStylesBySize.medium}
        color: ${inverse ? colors.whiteDuplicate : colors.black};
        border: 3px solid ${inverse ? "#3B3B46" : colors.veryLightPink};
        font-family: inherit;
        width: 100%;
        background-color: ${inverse ? "transparent" : colors.whiteDuplicate};

        @media (min-width: ${breakpoints.sm}rem) {
            ${size && inputStylesBySize[size.sm]}
        }

        @media (min-width: ${breakpoints.md}rem) {
            ${size && inputStylesBySize[size.md]}
        }

        @media (min-width: ${breakpoints.lg}rem) {
            ${size && inputStylesBySize[size.lg]}
        }

        &::placeholder {
            color: ${inverse ? colors.brownGrey : colors.brownishGreyTwo};
        }

        &:hover {
            border: 2px solid
                ${inverse ? colors.brownGrey : colors.brownishGreyTwo};
        }

        &:focus {
            border: 3px solid ${
                inverse ? "transparent" : colors.whiteDuplicate
            };
            outline: 3px auto rgba(40, 143, 240, 1);
        }

        &:disabled {
            color: ${inverse ? colors.brownGrey : colors.brownishGreyTwo};
            background-color: ${
                inverse ? colors.charcoalGreyTwo : colors.veryLightPink
            };
            border: 1px solid
                ${
                    inverse
                        ? colors.charcoalGrey
                        : colors.veryLightPinkTwoDuplicate
                };
        }

        &:read-only {
            border: 3px solid ${
                inverse ? "transparent" : colors.whiteDuplicate
            };
            color: ${inverse ? colors.whiteDuplicate : colors.black};
        }
    `}
`;

export const InputWrapper = styled.div`
    position: relative;
    width: 100%;
`;

// label
export const Label = styled.label`
    color: ${colors.brownishGreyTwo};
`;

// reusable
export const AccentBorder = styled.div`
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 3px;
`;

// success state
export const SuccessBorder = styled(AccentBorder)`
    background-color: ${colors.aquaGreenTwo};
`;

export const SuccessIconWrapper = styled.div`
    position: absolute;
    right: 0.8875rem;
    top: 50%;
    margin-top: -0.625rem;
`;

export const StyledCheckMarkApprove = styled(CheckMarkApprove)`
    width: 1rem;
    fill: ${({ inverse }) => (inverse ? "#86FFC5" : colors.jungleGreen)};
`;

let SuccessIcon = ({ inputSuccessful, inverse, className }) => (
    <SuccessIconWrapper inputSuccessful={inputSuccessful} className={className}>
        <StyledCheckMarkApprove inverse={inverse} />
    </SuccessIconWrapper>
);

SuccessIcon = styled(SuccessIcon)``;
export { SuccessIcon };

// error state
export const ErrorBorder = styled(AccentBorder)`
    background-color: ${({ inverse }) => (inverse ? "#FFA7A7" : colors.coral)};
`;

export const ErrorMessage = styled.div`
    color: ${({ inverse }) => (inverse ? "#FFA7A7" : colors.coral)};
`;

// wrappers
export const LabelAndErrorWrapper = styled.div`
    position: absolute;
    font-size: 0.75rem;
    padding-top: 0.25rem;
`;

export const StyledFormInput = styled.div`
    position: relative;

    /* Mobile-only, up to 767px: */
    @media (max-width: 47.94rem) {
        /* using breakpoints here isnt working for some reason */
        width: 100%;
    }
`;

// misc
export const PlaceholderContent = styled.div``;
